import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Icon,
  IconButton,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import {
  getQuoteCustomersGridApi,
  getQuoteProGroupsAttributeApi,
  getEavAttributeApi,
  getQuoteCustomersCountByMarketingSegmentsApi
} from 'api';
import { useStyles } from '../styles';
import IndividualPatientResultTable from './IndividualPatientResultTable';
import GroupPatientResultTable from './GroupPatientResultTable';
import SearchPatientField from './SearchPatientField';
import SearchGroupField from './SearchGroupField';


const SearchPatient = ({ open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userStoreData: {storeCode} } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';

  const {
    quoteCustomers,
    customersTotalCount,
    quoteGroupsAttributeData,
    loadingQuoteCustomers,
    loadingCustomerQuotes
  } = useSelector((state: IReduxState) => state.quote);
  const {
    options,
    isEavAttributeLoading
  } = useSelector((state: IReduxState) => state.eavAttributes);

  const loading = loadingQuoteCustomers || loadingCustomerQuotes || isEavAttributeLoading;

  const [tabValue, setTabValue] = useState('individual');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterConditions] = useState<IFilterConditions>({});
  const [searchConditions, setSearchConditions] = useState({});
  const [marketingSegments, setMarketingSegments] = useState([]);

  useEffect(() => {
    if (!quoteGroupsAttributeData.options.length) {
      getQuoteProGroupsAttributeApi({ dispatch });
    }

    if (!options?.length) {
      getEavAttributeApi({
        dispatch,
        entityType: 1,
        attributeCode: 'marketing_segments'
      });
    }
  }, []);

  useEffect(() => {
    getQuoteCustomersGridApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  useEffect(() => {
    const requiredTabValue = `${isCustomer3 ? 'customer' : 'patient'}_group`;
    setMarketingSegments(options);

    if (tabValue === requiredTabValue) {
      getQuoteCustomersCountByMarketingSegmentsApi({
        dispatch,
        sortDirection,
        sortField,
        pageSize: 1,
        currentPage,
        searchConditions,
        field: 'marketing_segments',
        conditionType: 'like',
        marketingSegments: options
      });
    }
  }, [tabValue]);

  const handleCloseModal = () => {
    onSearchPatient('', 'name');
    onSearchGroup('');
    onClose();
  };

  const onSearchPatient = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const onSearchGroup = (searchValue) => {
    const filteredMarketingSegments = options.filter((group) =>
      group.label.toLowerCase().includes(searchValue.toLowerCase())
    );
    setMarketingSegments(filteredMarketingSegments);
    setCurrentPage(1);
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <span>{`Search ${isCustomer3 ? 'Customer' : 'Patient'}`}</span>
        <IconButton
          edge="end"
          color="inherit"
          size="small"
          onClick={handleCloseModal}
          className={classes.closeButton}
          aria-label="close"
        >
          <Icon fontSize="small" className={classes.closeIcon}>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent className="mx-8 mb-8">
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          classes={{
            indicator: classes.tabIndicator,
            flexContainer: classes.fullWidthTabs
          }}
        >
          <Tab label="Individual" value="individual" className={tabValue === 'individual' ? classes.activeTab : classes.inactiveTab} />
          <Tab
            label={'Marketing Segment'}
            value={`${isCustomer3 ? 'customer' : 'patient'}_group`}
            className={`${tabValue === (isCustomer3 ? 'customer_group' : 'patient_group') ? classes.activeTab : classes.inactiveTab}`}
          />
        </Tabs>
        <Divider />

        {tabValue === 'individual' &&
          <SearchPatientField
            onSearch={onSearchPatient}
            isCustomer3={isCustomer3}
          />
        }

        {tabValue === (isCustomer3 ? 'customer_group' : 'patient_group') &&
          <SearchGroupField onSearch={onSearchGroup}  />
        }

        {tabValue === 'individual' && (
          quoteCustomers.length === 0 ? (
            <div>
              <Typography variant="body1" align="center">{`${isCustomer3 ? 'Customer' : 'Patient'}s not found.`}</Typography>
            </div>
          ) : (
            <IndividualPatientResultTable
              customers={quoteCustomers}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              sortField={sortField}
              setSortField={setSortField}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              customersTotalCount={customersTotalCount}
              handleCloseModal={handleCloseModal}
            />
          )
        )}

        {tabValue === (isCustomer3 ? 'customer_group' : 'patient_group') && (
          <GroupPatientResultTable
            patientGroups={marketingSegments}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            handleCloseModal={handleCloseModal}
          />
        )}
      </DialogContent>
      <BackdropCircular loading={loading} />
    </Dialog>
  );
};

export default SearchPatient;
